import {PropsWithChildren, useEffect, useState} from "react";
import classNames from "classnames";

import {Button} from "@pg-design/button-module";
import {Col, Row} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT} from "../../constants/property_card_detail_offer_info_content";
import {IKeyVariable, IVariable} from "../../types/IVariable";
import {IPropertyDetailDTO} from "../../types/property/IPropertyDetailDTO";
import {
    formatParkingPlaceText,
    getFacilitiesString,
    getRoomHeightText,
    getStandardString,
    getStorageText
} from "../../utils/property_card_detail_offer_info_utils";
import {HomematchPropertyCardDetailOfferInfoDescription} from "./HomematchPropertyCardDetailOfferInfoDescription";

import * as styles from "./HomematchPropertyCardDetailOfferInfo.module.css";

interface IProps {
    disableShowMoreButton?: boolean;
    facilitiesInfo?: IVariable[];
    onShowAll?: () => void;
    onShowMore?: () => void;
    parkingPlacesTypes?: IKeyVariable[];
    property: IPropertyDetailDTO;
    standardDescriptions?: IKeyVariable[];
}

export const HomematchPropertyCardDetailOfferInfo = (props: IProps) => {
    const {
        disableShowMoreButton = false,
        facilitiesInfo = [],
        onShowAll = () => undefined,
        onShowMore = () => undefined,
        parkingPlacesTypes = [],
        property,
        standardDescriptions = []
    } = props;

    const [showDesc, setShowDesc] = useState(false);

    const {isMobile} = useUserDevice();

    const offer = property.offer;
    const region = property.region;
    const type = property.type || property.offer.type;

    const facilitiesString = getFacilitiesString(offer, facilitiesInfo);
    const hasParkingPlaces = Boolean(offer.parking_places?.length);
    const standard = getStandardString(offer, standardDescriptions);

    const facilitiesText = facilitiesString ? capitalize(facilitiesString) : PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const heatingText = offer.standard_description?.heating
        ? capitalize(offer.standard_description.heating)
        : PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const roomHeightText = getRoomHeightText(offer) ?? PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const standardText = standard ?? PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const storageText = getStorageText(offer, region.country);

    useEffect(() => {
        setShowDesc(false);
    }, [offer]);

    const handleShowAllClick = () => {
        onShowAll();
    };

    const handleShowMoreClick = () => {
        onShowMore();
        setShowDesc(true);
    };

    const labelVariant = isMobile ? "body_copy_1" : "info_txt_1";
    const labelClassName = classNames(styles.rowLabel, styles.rowLabelDesktop);

    return (
        <section className={styles.section}>
            <Text as="p" className={styles.miniHeader} variant="mini_header">
                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.TITLE}
            </Text>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.PARKING_PLACES_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {hasParkingPlaces ? (
                    <>
                        {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.PARKING_PLACES_INTRO}
                        {offer.parking_places.map((parkingPlace, index) => (
                            <span className={styles.displayBlock} key={index}>
                                {formatParkingPlaceText(parkingPlace, parkingPlacesTypes, region.country)}
                            </span>
                        ))}
                    </>
                ) : (
                    PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.NO_PARKING_PLACES
                )}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.STORAGE_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {storageText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.STANDARD_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {standardText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.HEATING_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {heatingText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.ROOM_HEIGHT_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {roomHeightText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isLast
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.FACILITIES_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {facilitiesText}
            </HomematchPropertyCardDetailOfferInfoRow>

            {!showDesc && (
                <Button className={classNames(styles.mt2, styles.ph3)} onClick={handleShowMoreClick} size="x-small" variant="outlined_secondary">
                    {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.MORE_INFO_BUTTON}
                </Button>
            )}

            {showDesc && (
                <div>
                    <HomematchPropertyCardDetailOfferInfoDescription offer={offer} region={region} type={type} />

                    {!disableShowMoreButton && (
                        <>
                            <Text strong variant="body_copy_1">
                                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.AVAILABLE_PROPERTIES(offer.stats?.properties_count_for_sale ?? 0)}
                            </Text>

                            <Button
                                className={classNames(styles.mt2, styles.ph4)}
                                href={offer.url}
                                onClick={handleShowAllClick}
                                size="small"
                                target="_blank"
                                variant="outlined_secondary"
                            >
                                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.SEE_ALL_BUTTON}
                            </Button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

interface IOfferInfoRowProps extends PropsWithChildren {
    isLast?: boolean;
    isMobile: boolean;
    label: string;
    labelClassName: string;
    labelVariant: "body_copy_1" | "info_txt_1";
    valueVariant?: "body_copy_1" | "body_copy_2";
}

const HomematchPropertyCardDetailOfferInfoRow = (props: IOfferInfoRowProps) => {
    const {children, isLast, isMobile, label, labelClassName, labelVariant, valueVariant = "body_copy_2"} = props;

    return (
        <Row className={isLast ? styles.lastRow : styles.row}>
            <Col sm={isMobile ? 4 : 1}>
                <Text as="p" className={labelClassName} color="var(--colors-gray-700)" variant={labelVariant}>
                    {label}
                </Text>
            </Col>

            <Col sm={isMobile ? 4 : 3}>
                <Text as="p" variant={valueVariant}>
                    {children}
                </Text>
            </Col>
        </Row>
    );
};
import {PropsWithChildren, useEffect, useState} from "react";
import classNames from "classnames";

import {Button} from "@pg-design/button-module";
import {Col, Row} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT} from "../../constants/property_card_detail_offer_info_content";
import {IKeyVariable, IVariable} from "../../types/IVariable";
import {IPropertyDetailDTO} from "../../types/property/IPropertyDetailDTO";
import {
    formatParkingPlaceText,
    getFacilitiesString,
    getRoomHeightText,
    getStandardString,
    getStorageText
} from "../../utils/property_card_detail_offer_info_utils";
import {HomematchPropertyCardDetailOfferInfoDescription} from "./HomematchPropertyCardDetailOfferInfoDescription";

import * as styles from "./HomematchPropertyCardDetailOfferInfo.module.css";

interface IProps {
    disableShowMoreButton?: boolean;
    facilitiesInfo?: IVariable[];
    onShowAll?: () => void;
    onShowMore?: () => void;
    parkingPlacesTypes?: IKeyVariable[];
    property: IPropertyDetailDTO;
    standardDescriptions?: IKeyVariable[];
}

export const HomematchPropertyCardDetailOfferInfo = (props: IProps) => {
    const {
        disableShowMoreButton = false,
        facilitiesInfo = [],
        onShowAll = () => undefined,
        onShowMore = () => undefined,
        parkingPlacesTypes = [],
        property,
        standardDescriptions = []
    } = props;

    const [showDesc, setShowDesc] = useState(false);

    const {isMobile} = useUserDevice();

    const offer = property.offer;
    const region = property.region;
    const type = property.type || property.offer.type;

    const facilitiesString = getFacilitiesString(offer, facilitiesInfo);
    const hasParkingPlaces = Boolean(offer.parking_places?.length);
    const standard = getStandardString(offer, standardDescriptions);

    const facilitiesText = facilitiesString ? capitalize(facilitiesString) : PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const heatingText = offer.standard_description?.heating
        ? capitalize(offer.standard_description.heating)
        : PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const roomHeightText = getRoomHeightText(offer) ?? PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const standardText = standard ?? PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.INFO_FROM_VENDOR;
    const storageText = getStorageText(offer, region.country);

    useEffect(() => {
        setShowDesc(false);
    }, [offer]);

    const handleShowAllClick = () => {
        onShowAll();
    };

    const handleShowMoreClick = () => {
        onShowMore();
        setShowDesc(true);
    };

    const labelVariant = isMobile ? "body_copy_1" : "info_txt_1";
    const labelClassName = classNames(styles.rowLabel, styles.rowLabelDesktop);

    return (
        <section className={styles.section}>
            <Text as="p" className={styles.miniHeader} variant="mini_header">
                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.TITLE}
            </Text>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.PARKING_PLACES_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {hasParkingPlaces ? (
                    <>
                        {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.PARKING_PLACES_INTRO}
                        {offer.parking_places.map((parkingPlace, index) => (
                            <span className={styles.displayBlock} key={index}>
                                {formatParkingPlaceText(parkingPlace, parkingPlacesTypes, region.country)}
                            </span>
                        ))}
                    </>
                ) : (
                    PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.NO_PARKING_PLACES
                )}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.STORAGE_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {storageText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.STANDARD_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {standardText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.HEATING_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {heatingText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.ROOM_HEIGHT_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {roomHeightText}
            </HomematchPropertyCardDetailOfferInfoRow>

            <HomematchPropertyCardDetailOfferInfoRow
                isLast
                isMobile={Boolean(isMobile)}
                label={PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.FACILITIES_LABEL}
                labelClassName={labelClassName}
                labelVariant={labelVariant}
            >
                {facilitiesText}
            </HomematchPropertyCardDetailOfferInfoRow>

            {!showDesc && (
                <Button className={classNames(styles.mt2, styles.ph3)} onClick={handleShowMoreClick} size="x-small" variant="outlined_secondary">
                    {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.MORE_INFO_BUTTON}
                </Button>
            )}

            {showDesc && (
                <div>
                    <HomematchPropertyCardDetailOfferInfoDescription offer={offer} region={region} type={type} />

                    {!disableShowMoreButton && (
                        <>
                            <Text strong variant="body_copy_1">
                                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.AVAILABLE_PROPERTIES(offer.stats?.properties_count_for_sale ?? 0)}
                            </Text>

                            <Button
                                className={classNames(styles.mt2, styles.ph4)}
                                href={offer.url}
                                onClick={handleShowAllClick}
                                size="small"
                                target="_blank"
                                variant="outlined_secondary"
                            >
                                {PROPERTY_CARD_DETAIL_OFFER_INFO_CONTENT.SEE_ALL_BUTTON}
                            </Button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

interface IOfferInfoRowProps extends PropsWithChildren {
    isLast?: boolean;
    isMobile: boolean;
    label: string;
    labelClassName: string;
    labelVariant: "body_copy_1" | "info_txt_1";
    valueVariant?: "body_copy_1" | "body_copy_2";
}

const HomematchPropertyCardDetailOfferInfoRow = (props: IOfferInfoRowProps) => {
    const {children, isLast, isMobile, label, labelClassName, labelVariant, valueVariant = "body_copy_2"} = props;

    return (
        <Row className={isLast ? styles.lastRow : styles.row}>
            <Col sm={isMobile ? 4 : 1}>
                <Text as="p" className={labelClassName} color="var(--colors-gray-700)" variant={labelVariant}>
                    {label}
                </Text>
            </Col>

            <Col sm={isMobile ? 4 : 3}>
                <Text as="p" variant={valueVariant}>
                    {children}
                </Text>
            </Col>
        </Row>
    );
};
